import { useEffect, useMemo, useRef } from "react";
import { trpc } from "@org/clients/school";
import { Block, ContentItem } from "@org/ui";
import { unwrapTrpcData } from "@org/ui";
import {
  ContentDefinitionRequest,
  RawContentItem,
  unwrap,
  extractFirstRecord,
  buildContentDefinitionQuery,
  hydrateContentItem,
} from "./content-hydrators";

type UseBlocksDataProps = {
  classId: string;
  isEditing: boolean;
  onBlocksLoaded: (blocks: Block[]) => void;
  saveSignal?: number;
};

export const useBlocksData = ({
  classId,
  isEditing,
  onBlocksLoaded,
  saveSignal = 0
}: UseBlocksDataProps) => {
  const processedClassIdRef = useRef<string | null>(null);
  const lastSaveSignalRef = useRef<number>(0);
  const shouldRefetchRef = useRef<boolean>(false);
  const contentDefinitionRequestsRef = useRef<ContentDefinitionRequest[]>([]);

  // Fetch blocks for the class
  const blocksQuery = trpc.blocks.getBlocksByClass.useQuery(
    { classId: classId || "" },
    { enabled: !!classId && classId.trim() !== "" }
  );

  const normalizedBlocks = useMemo(
    () => unwrapTrpcData(blocksQuery.data) ?? [],
    [blocksQuery.data]
  );

  // Fetch contents for each block
  const blockContentQueries = trpc.useQueries(t =>
    normalizedBlocks.length
      ? normalizedBlocks.map(block =>
          t.blocks.getBlockContents(
            { blockId: block.blockId },
            { enabled: !!block.blockId && block.blockId.trim() !== "" }
          )
        )
      : []
  );

  // Fetch content definitions (type-specific data) for each content item
  const contentQueries = trpc.useQueries(t => {
    if (!blockContentQueries.every(query => query.data)) {
      contentDefinitionRequestsRef.current = [];
      return [];
    }

    const requests: ContentDefinitionRequest[] = [];

    const queryConfigs = normalizedBlocks.flatMap((_, blockIndex) => {
      const contents = unwrap(blockContentQueries[blockIndex]?.data) ?? [];

      return (Array.isArray(contents) ? contents : []).map((contentItem) => {
        const item = unwrap(contentItem) as RawContentItem | undefined;
        const content = item?.content;

        if (!content?.contentId || !content?.contentType) return null;

        const request: ContentDefinitionRequest = {
          contentId: content.contentId,
          contentType: content.contentType,
        };

        const queryConfig = buildContentDefinitionQuery(t, request);
        if (!queryConfig) return null;

        requests.push(request);
        return queryConfig;
      }).filter(Boolean);
    });

    contentDefinitionRequestsRef.current = requests;
    return queryConfigs;
  });

  const isLoadingContents = blockContentQueries.some(query => query.isLoading);
  const isLoadingDefinitions = contentQueries.some(query => query.isLoading);
  const isLoading = blocksQuery.isLoading || isLoadingContents || isLoadingDefinitions;

  // Refetch all queries when saveSignal changes
  useEffect(() => {
    if (saveSignal > lastSaveSignalRef.current) {
      processedClassIdRef.current = null;
      shouldRefetchRef.current = true;

      blocksQuery.refetch();
      blockContentQueries.forEach(query => {
        if (query.refetch) query.refetch();
      });
      contentQueries.forEach(query => {
        if (query && typeof query.refetch === "function") query.refetch();
      });
    }

    lastSaveSignalRef.current = saveSignal;
  }, [saveSignal, blocksQuery, blockContentQueries, contentQueries]);

  // Process and hydrate block data when ready
  useEffect(() => {
    if (
      isLoading ||
      !normalizedBlocks.length ||
      !isEditing ||
      !classId ||
      (processedClassIdRef.current === classId && !shouldRefetchRef.current)
    ) {
      return;
    }

    shouldRefetchRef.current = false;

    try {
      const definitionsByContentId = new Map<string, unknown>();
      contentDefinitionRequestsRef.current.forEach((request, index) => {
        const query = contentQueries[index];
        if (query?.status !== "success") return;

        const definition = extractFirstRecord(query.data);
        if (definition !== null && definition !== undefined) {
          definitionsByContentId.set(request.contentId, definition);
        }
      });

      const blocksWithContent: Block[] = normalizedBlocks.map((block, blockIndex) => {
        const rawContents = unwrap(blockContentQueries[blockIndex]?.data) ?? [];
        const transformedContents = (Array.isArray(rawContents) ? rawContents : [])
          .map((contentItem) => hydrateContentItem(contentItem, definitionsByContentId))
          .filter((item): item is ContentItem => Boolean(item))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        return {
          id: block.blockId,
          name: block.name,
          description: block.description || "",
          order: block.order,
          contents: transformedContents,
          type: "block",
        };
      });

      onBlocksLoaded(blocksWithContent);
      processedClassIdRef.current = classId;
    } catch (error) {
      console.error("Error processing block data:", error);
    }
  }, [
    normalizedBlocks,
    blockContentQueries,
    contentQueries,
    isLoading,
    isEditing,
    classId,
    onBlocksLoaded,
    saveSignal
  ]);

  return {
    processedBlocks: normalizedBlocks,
    isLoading
  };
};
